import React, { useEffect, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Card,
  CardContent,
  Switch,
  Button,
  CircularProgress,
  Divider,
  Badge,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import DinamikFormConfigAPI from "../../../apis/OB/Get/DinamikFormConfigAPI";

interface SayfaItem {
  sayfaKodu: string;
  sayfaAdi: string;
  alanSayisi: number;
}

interface AlanItem {
  alanId: number;
  alanKodu: string;
  alanEtiketi: string;
  inputTipiKodu: string;
  gorunurMu: string;
  zorunluMu: string;
  siraNo: number;
  grupKodu: string | null;
  grupEtiketi: string | null;
  placeholder: string | null;
  kolonGenisligi: string;
  zorunluHataMesaji: string | null;
  saltOkunurMu: string;
  apiEndpoint: string | null;
  bagliAlanKodu: string | null;
}

const DinamikFormYonetim: React.FC = () => {
  const [sayfalar, setSayfalar] = useState<SayfaItem[]>([]);
  const [selectedSayfa, setSelectedSayfa] = useState<string | null>(null);
  const [alanlar, setAlanlar] = useState<AlanItem[]>([]);
  const [originalAlanlar, setOriginalAlanlar] = useState<AlanItem[]>([]);
  const [loadingSayfalar, setLoadingSayfalar] = useState(true);
  const [loadingAlanlar, setLoadingAlanlar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Sayfa listesini yukle
  useEffect(() => {
    setLoadingSayfalar(true);
    DinamikFormConfigAPI.getSayfaListesi()
      .then((res: any) => {
        if (res?.data && Array.isArray(res.data)) {
          setSayfalar(res.data);
          if (res.data.length > 0) {
            setSelectedSayfa(res.data[0].sayfaKodu);
          }
        }
      })
      .catch(() => {
        toast.error("Sayfa listesi yuklenemedi.");
      })
      .finally(() => setLoadingSayfalar(false));
  }, []);

  // Secili sayfanin alanlarini yukle
  useEffect(() => {
    if (!selectedSayfa) return;
    setLoadingAlanlar(true);
    setIsDirty(false);

    DinamikFormConfigAPI.getFormConfig(selectedSayfa)
      .then((res: any) => {
        if (res?.data?.alanlar) {
          const mapped = res.data.alanlar.map((a: any) => ({
            alanId: a.alanId || 0,
            alanKodu: a.alanKodu,
            alanEtiketi: a.alanEtiketi,
            inputTipiKodu: a.inputTipiKodu,
            gorunurMu: a.gorunurMu || "E",
            zorunluMu: a.zorunluMu || "H",
            siraNo: a.siraNo || 0,
            grupKodu: a.grupKodu,
            grupEtiketi: a.grupEtiketi,
            placeholder: a.placeholder,
            kolonGenisligi: a.kolonGenisligi || "6",
            zorunluHataMesaji: a.zorunluHataMesaji,
            saltOkunurMu: a.saltOkunurMu || "H",
            apiEndpoint: a.apiEndpoint,
            bagliAlanKodu: a.bagliAlanKodu,
          }));
          setAlanlar(mapped);
          setOriginalAlanlar(JSON.parse(JSON.stringify(mapped)));
        } else {
          setAlanlar([]);
          setOriginalAlanlar([]);
        }
      })
      .catch(() => {
        toast.error("Alan konfigurasyonu yuklenemedi.");
        setAlanlar([]);
      })
      .finally(() => setLoadingAlanlar(false));
  }, [selectedSayfa]);

  // Mevcut getByPageCode endpointi alanId donmuyor, bunu eklemek gerekiyor
  // Simdilik alanKodu uzerinden calisacak, backend guncellenmisse alanId kullanilacak

  const handleAlanChange = (index: number, field: keyof AlanItem, value: any) => {
    setAlanlar((prev) => {
      const updated = [...prev];
      (updated[index] as any)[field] = value;
      return updated;
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!selectedSayfa) return;
    setSaving(true);

    const request = {
      sayfaKodu: selectedSayfa,
      alanlar: alanlar.map((a) => ({
        alanId: a.alanId,
        alanEtiketi: a.alanEtiketi,
        gorunurMu: a.gorunurMu,
        zorunluMu: a.zorunluMu,
        siraNo: a.siraNo,
        placeholder: a.placeholder,
        kolonGenisligi: a.kolonGenisligi,
        zorunluHataMesaji: a.zorunluHataMesaji,
        saltOkunurMu: a.saltOkunurMu,
      })),
    };

    try {
      const res: any = await DinamikFormConfigAPI.updateAlanlar(request);
      if (res?.data) {
        toast.success(`${res.data.guncellenen} alan basariyla guncellendi.`);
        setOriginalAlanlar(JSON.parse(JSON.stringify(alanlar)));
        setIsDirty(false);
      } else {
        toast.error("Guncelleme sirasinda hata olustu.");
      }
    } catch {
      toast.error("Guncelleme sirasinda hata olustu.");
    } finally {
      setSaving(false);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [sayfaSearchTerm, setSayfaSearchTerm] = useState("");

  // Arama filtresi
  const filteredAlanlar = alanlar.filter((alan) =>
    alan.alanEtiketi.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedSayfaInfo = sayfalar.find((s) => s.sayfaKodu === selectedSayfa);

  return (
    <Box sx={{ display: "flex", minHeight: 500, height: "calc(100vh - 300px)", gap: 2 }}>
      {/* Sol Panel - Sayfa Listesi */}
      <Card sx={{ width: 280, minWidth: 280, overflow: "auto" }}>
        <CardContent sx={{ p: 1 }}>
          <input
            type="text"
            placeholder="SAYFA ARA"
            value={sayfaSearchTerm}
            onChange={(e) => setSayfaSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "0.85rem",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <Divider sx={{ mt: 1 }} />
          {loadingSayfalar ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List dense>
              {sayfalar.filter((s) =>
                s.sayfaAdi.toLowerCase().includes(sayfaSearchTerm.toLowerCase()) ||
                s.sayfaKodu.toLowerCase().includes(sayfaSearchTerm.toLowerCase())
              ).map((s) => (
                <ListItemButton
                  key={s.sayfaKodu}
                  selected={selectedSayfa === s.sayfaKodu}
                  onClick={() => {
                    if (isDirty) {
                      if (!window.confirm("Kaydedilmemis degisiklikler var. Devam etmek istiyor musunuz?")) return;
                    }
                    setSelectedSayfa(s.sayfaKodu);
                  }}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={s.sayfaAdi}
                    primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: selectedSayfa === s.sayfaKodu ? "bold" : "normal" }}
                    secondaryTypographyProps={{ fontSize: "0.7rem" }}
                  />
                  <Badge badgeContent={s.alanSayisi} color="primary" sx={{ ml: 1 }} />
                </ListItemButton>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Sag Panel - Alan Konfigurasyonu */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, position: "sticky", top: 0, zIndex: 10, bgcolor: "background.default", py: 1,px: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="h6">
            {selectedSayfaInfo ? `${selectedSayfaInfo.sayfaAdi}` : "Sayfa Seciniz"}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={!isDirty || saving}
            onClick={handleSave}
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Box>

        {loadingAlanlar ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : alanlar.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 3 }}>
            {selectedSayfa ? "Bu sayfa icin konfigurasyon bulunamadi." : "Sol taraftan bir sayfa seciniz."}
          </Typography>
        ) : (
          <Card>
            <CardContent sx={{ p: 2 }}>
              {/* Arama */}
              <input
                type="text"
                placeholder="Alan adina gore ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  marginBottom: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "0.85rem",
                  outline: "none",
                }}
              />

              {/* Baslik Satiri */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, px: 1 }}>
                <Box sx={{ flex: 1 }}><Typography variant="caption" fontWeight="bold">Alan Adi</Typography></Box>
                <Box sx={{ width: 70, textAlign: "center" }}><Typography variant="caption" fontWeight="bold">Pasif/Aktif</Typography></Box>
                <Box sx={{ width: 70, textAlign: "center" }}><Typography variant="caption" fontWeight="bold">Zorunlu</Typography></Box>
              </Box>
              <Divider sx={{ mb: 1 }} />

              {filteredAlanlar.map((alan) => {
                const originalIndex = alanlar.indexOf(alan);
                return (
                  <Box key={alan.alanKodu} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, px: 1, "&:hover": { bgcolor: "action.hover" }, borderRadius: 1 }}>
                    <Typography sx={{ flex: 1, fontSize: "0.85rem" }}>
                      {alan.alanEtiketi}
                    </Typography>
                    <Box sx={{ width: 70, textAlign: "center" }}>
                      <Switch
                        size="small"
                        checked={alan.gorunurMu === "E"}
                        onChange={(e) => {
                          const aktif = e.target.checked;
                          handleAlanChange(originalIndex, "gorunurMu", aktif ? "E" : "H");
                          if (!aktif) {
                            handleAlanChange(originalIndex, "zorunluMu", "H");
                          }
                        }}
                        color="success"
                      />
                    </Box>
                    <Box sx={{ width: 70, textAlign: "center" }}>
                      <Switch
                        size="small"
                        checked={alan.zorunluMu === "E"}
                        disabled={alan.gorunurMu === "H"}
                        onChange={(e) => handleAlanChange(originalIndex, "zorunluMu", e.target.checked ? "E" : "H")}
                        color="warning"
                      />
                    </Box>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default DinamikFormYonetim;
